import { useContext, useEffect, useState } from 'react';
import { debounce } from 'lodash-es';
import axios from '@lambda/commons/apis/axios';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import {
  ProductGroup,
  PopularProductGroup,
} from '@lambda/apis/src/product-group/types';
import Loading from '@/components/Loading/Loading';
import useLoading from '@/lib/use-loading';
import { SearchResultsOnlyOffers } from '@/lib/search-helpers';
import { QsContext } from '@/lib/use-qs';
import i18n from '@/settings/i18n';
import CollectionItemBox from '../collections/CollectionItemBox';
import OfferCard from '@/components/collections/OfferCard';

const t = i18n.init();

interface Props {
  offers: Array<SearchOffers>;
  hasNextPage: boolean;
  isCollection: boolean;
  productGroup?: ProductGroup;
  popularProductGroups?: PopularProductGroup[];
}

export default function InfiniteScrollSearchResults(props: Props) {
  const [qs] = useContext(QsContext);
  const [offers, setOffers] = useState(props.offers);
  const [hasNextPage, setHasNextPage] = useState(props.hasNextPage);
  const [nextPage, setNextPage] = useState(2);

  const [loading, loadMore] = useLoading(async (page: number) => {
    const categories = props.productGroup?.filters?.categories
      ? props.productGroup.filters.categories[0]
      : undefined;
    const urlQs =
      props.isCollection && categories
        ? new URLSearchParams({
            ...qs,
            page: page.toString(),
            categories: qs?.categories ? qs?.categories : categories,
          })
        : new URLSearchParams({
            ...qs,
            page: page.toString(),
          });

    const brand = props.productGroup?.filters?.brand
      ? props.productGroup.filters.brand[0]
      : undefined;
    const url = brand
      ? `/api/search-results?${urlQs}&brand=${brand}`
      : `/api/search-results?${urlQs}`;

    const response = await axios.get<SearchResultsOnlyOffers>(url);

    setOffers((o) => [...o, ...response.data.offers]);
    setNextPage(page + 1);
    setHasNextPage(response.data.hasNextPage);
  });

  useEffect(() => setOffers(props.offers), [props.offers]);
  useEffect(() => setHasNextPage(props.hasNextPage), [props.hasNextPage]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (window === undefined) return;

      const resultsEl = document.querySelector(
        '#e2e-search-results',
      ) as Element;

      if (resultsEl == null) return;

      const elementScrollHeight = resultsEl.scrollHeight;

      if (
        window.pageYOffset + window.innerHeight > elementScrollHeight &&
        hasNextPage &&
        !loading
      )
        loadMore(nextPage);
    }, 50);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [nextPage, hasNextPage, loading]);

  return (
    <>
      {!offers.length && (
        <>
          <h3 className="mt-3 flex items-center justify-center text-xl font-bold text-black lg:hidden">
            {t`Search results`}
          </h3>
          <p className="pt-2 text-center text-sm text-black sm:text-base lg:pt-4 lg:text-left">
            {t`Sorry, no products matched your selection.`}
          </p>
          <p className="pt-2 text-center text-sm text-black sm:text-base lg:pt-3 lg:text-left">
            {t`Try modifying your search or take a look at some of these reccommendations below.`}
          </p>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:mt-6 xl:grid-cols-4">
            {props.popularProductGroups?.map((collection) => (
              <CollectionItemBox
                key={collection.handle}
                link={{
                  pathname: `/collections/${collection.handle}`,
                }}
                image={collection.summary?.imageUrl as string}
                title={collection.summary?.title ?? ''}
                price={collection.summary?.totalPrice || 0}
              />
            ))}
          </div>
        </>
      )}
      <div
        className="mt-5 grid grid-cols-1 gap-2.5 3xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:mt-5 lg:grid-cols-3 xl:grid-cols-4"
        id="e2e-search-results"
      >
        {offers.map((offer) => (
          <OfferCard offer={offer} key={offer.key} />
        ))}
      </div>

      {loading && (
        <div className="pt-6 text-center">
          <Loading />
        </div>
      )}
    </>
  );
}
